import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/mangaData';

const aboutJsonLd = [
  {
    '@type': 'Organization',
    name: '刃牙漫画',
    url: 'https://readdy.ai',
    description: '刃牙漫画免费全集在线阅读平台，下拉式阅读，全部章节一次畅读',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
  },
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  },
  {
    '@type': 'FAQPage',
    mainEntity: aboutInfo.features.map((f) => ({
      '@type': 'Question',
      name: f.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.desc,
      },
    })),
  },
];

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormSuccess(true);
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9oo6v2ftmtnfcfh0asg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormError(msg.includes('spam') ? '提交失败，请稍后再试' : msg);
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 刃牙漫画 | 品牌介绍与联系方式"
        description="了解刃牙漫画免费阅读平台的品牌故事、核心优势与联系方式。刃牙漫画免费全集在线阅读，下拉式即时阅读，全部章节一次畅读。"
        keywords="刃牙漫画关于我们,刃牙漫画品牌介绍,刃牙漫画联系方式"
        canonicalUrl="https://readdy.ai/about"
        jsonLd={aboutJsonLd}
      />

      <main className="min-h-screen pt-20">
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于刃牙漫画
              </h1>
              <p className="text-foreground-600 leading-relaxed">
                {aboutInfo.brandIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                我们的使命
              </h2>
              <p className="text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                读者评价
              </h2>
              <p className="text-foreground-500">来自刃牙粉丝的真实评价</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500">有任何问题或建议？欢迎联系我们</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-0.5">
                      <a href="#">电子邮箱</a>
                    </h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-0.5">
                      <a href="#">微信公众号</a>
                    </h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                    <i className="ri-qq-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-0.5">
                      <a href="#">QQ群</a>
                    </h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.qq}</p>
                  </div>
                </div>
              </div>

              <div>
                {formSuccess ? (
                  <div className="bg-background-100 rounded-lg p-8 text-center border border-background-200/70">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      提交成功！
                    </h3>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-lg p-6 border border-background-200/70 space-y-4"
                    data-readdy-form
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="contact-hp-field"
                    />
                    {formError && (
                      <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? '提交中...' : '发送留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}